import { createContext, useContext, useEffect, useState } from 'react';

// Global light/dark theme state. Adds/removes the `.dark` class on
// the root <html> element so Tailwind's darkMode: ['class'] strategy
// + the CSS custom properties in src/index.css flip the entire
// interior app (cards, inputs, buttons, sidebar) between themes.
//
// Pro Haul Red (#E22129) stays constant across both modes per the
// brand guide -- only the neutrals (bg/text/borders) invert.
//
// Theme choice persists to localStorage so the user's preference
// survives reloads. Default is 'dark' for first-time users (matches
// the app's established look). System-preference detection is
// deliberately omitted -- a TMS used during workday + nightshift
// shouldn't auto-flip based on OS time-of-day settings.
//
// Public auth + legal pages (CarrierAuth, AcceptInvite, LegalShell)
// hardcode the brand-white treatment via bg-prohaul-white tokens, so
// they stay light regardless of this toggle. The toggle only affects
// the authenticated interior dark/light theme.

const ThemeContext = createContext(null);
const STORAGE_KEY = 'prohaul.theme';

const readInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* localStorage disabled (Safari private mode) */ }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitialTheme);

  // Apply theme to the root element + persist on every change.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else                  root.classList.remove('dark');
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const setTheme    = (next) => setThemeState(next === 'light' ? 'light' : 'dark');
  const toggleTheme = () => setThemeState(t => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
